import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";

import { runSearchQuery } from "../../hooks/GASearchIndexHooks";
import {
  getFeedRankingConfig,
  updateFeedRankingConfig,
} from "../../../GAListenFeedSettings/hooks/ListenFeedSettingsHook";

/**
 * SearchLabPanel
 *
 * Admin tool (General Admin → QuickTales) to exercise the in-house search
 * endpoint (`searchQuickTales`) that replaces AddSearch. It shows live
 * type-ahead results as you type (debounced) and runs a full search on Enter,
 * so we can validate relevance/weightings before wiring the mobile app.
 *
 * Search field-weight overrides (Title/Narrator/Creator/Org/Description) are
 * "what-if" only — passed per request via configOverride — until "Apply
 * Settings" saves them to config/feedRanking (search.fieldWeights).
 *
 * See docs/ADDSEARCH_REPLACEMENT_PLAN.md (Phase 2.3).
 */
const DEBOUNCE_MS = 150;

// Search field-weight override keys + friendly labels.
const FIELD_KEYS = ["title", "narrator", "creator", "org", "description"];
const FIELD_LABELS = {
  title: "Title",
  narrator: "Narrator",
  creator: "Creator",
  org: "Org",
  description: "Description",
};

const SearchLabPanel = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState({ total: 0, tookMs: 0, mode: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Empty string = "use saved config value" for that field weight.
  const [weightOverrides, setWeightOverrides] = useState({});
  // Saved field weights (config/feedRanking.search.fieldWeights) shown in labels.
  const [savedWeights, setSavedWeights] = useState(null);
  const [applying, setApplying] = useState(false);

  const debounceRef = useRef(null);
  // Guards against out-of-order responses clobbering newer ones.
  const requestSeqRef = useRef(0);
  // Latest overrides, kept in a ref so debounced searches read current values.
  const overridesRef = useRef({});

  const handleWeightChange = (key, value) => {
    setWeightOverrides((prev) => {
      const next = { ...prev, [key]: value };
      overridesRef.current = next;
      return next;
    });
  };

  // Builds a { search: { fieldWeights } } override from the changed fields, or
  // null if nothing was changed.
  const buildConfigOverride = (overrides) => {
    const fieldWeights = {};
    FIELD_KEYS.forEach((k) => {
      const v = overrides[k];
      if (v !== "" && v !== undefined && v !== null && !Number.isNaN(Number(v))) {
        fieldWeights[k] = Number(v);
      }
    });
    return Object.keys(fieldWeights).length
      ? { search: { fieldWeights } }
      : undefined;
  };

  // Fetches the saved field weights so we can show current values next to each
  // override field label. Returns the weights (or null on failure).
  const loadSavedWeights = async () => {
    try {
      const cfg = await getFeedRankingConfig();
      const weights = cfg?.search?.fieldWeights || null;
      setSavedWeights(weights);
      return weights;
    } catch (e) {
      setSavedWeights(null);
      return null;
    }
  };

  useEffect(() => {
    loadSavedWeights();
  }, []);

  const doSearch = useCallback(async (q, mode) => {
    const trimmed = (q || "").trim();
    if (!trimmed) {
      setResults([]);
      setMeta({ total: 0, tookMs: 0, mode: "" });
      setError("");
      return;
    }
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError("");
    try {
      const data = await runSearchQuery({
        q: trimmed,
        mode,
        configOverride: buildConfigOverride(overridesRef.current),
      });
      // Ignore stale responses.
      if (seq !== requestSeqRef.current) return;
      setResults(data.results || []);
      setMeta({ total: data.total || 0, tookMs: data.tookMs || 0, mode });
    } catch (e) {
      if (seq !== requestSeqRef.current) return;
      console.error("Search Lab query failed:", e);
      setError(e.message || "Search failed");
      setResults([]);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(value, "typeahead");
    }, DEBOUNCE_MS);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      doSearch(query, "full");
    }
  };

  // Clears the search field and any results/errors.
  const handleClear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Bump the sequence so any in-flight response is ignored.
    requestSeqRef.current += 1;
    setQuery("");
    setResults([]);
    setMeta({ total: 0, tookMs: 0, mode: "" });
    setError("");
    setLoading(false);
  };

  // Re-runs the current query (if any) so override edits reflect immediately.
  const rerunCurrent = () => {
    if (query.trim()) doSearch(query, meta.mode === "full" ? "full" : "typeahead");
  };

  const handleReset = () => {
    setWeightOverrides({});
    overridesRef.current = {};
    rerunCurrent();
  };

  // Saves any changed field-weight overrides to config/feedRanking, refreshes
  // the saved values shown in the labels, then clears the override fields.
  const handleApply = async () => {
    const override = buildConfigOverride(weightOverrides);
    if (!override) return;
    setApplying(true);
    setError("");
    try {
      await updateFeedRankingConfig({
        search: override.search,
        updatedAt: new Date(),
      });
      await loadSavedWeights();
      setWeightOverrides({});
      overridesRef.current = {};
    } catch (e) {
      console.error("Search Lab apply settings failed:", e);
      setError(e.message || "Failed to save settings");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        backgroundColor: "#fafafa",
      }}
    >
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
        Search Lab (in-house search)
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#666", mb: 1.5 }}>
        Type to see live type-ahead results; press Enter for a full search.
        Exercises the <code>searchQuickTales</code> endpoint that replaces
        AddSearch.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", maxWidth: 640 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search title, narrator, creator, org…"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleClear}
          disabled={!query}
          sx={{ whiteSpace: "nowrap" }}
        >
          Clear
        </Button>
        {loading && <CircularProgress size={20} />}
      </Box>

      {/* Search field-weight overrides */}
      <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 2, mb: 1 }}>
        Search field weight overrides (blank = use saved value)
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        {FIELD_KEYS.map((key) => {
          const savedVal = savedWeights ? savedWeights[key] : undefined;
          const labelText = FIELD_LABELS[key] ?? key;
          return (
            <Box key={key} sx={{ flex: 1, minWidth: 120 }}>
              {/* Field name always shown above the field, with current saved
                  value in parentheses (e.g. "Title (5)"). */}
              <Typography
                sx={{ fontSize: 13, fontWeight: 600, mb: 0.5, color: "#333" }}
              >
                {labelText}
                {savedVal != null && ` (${savedVal})`}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={weightOverrides[key] ?? ""}
                inputProps={{ min: 0, step: 0.1 }}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Allow clearing (revert to saved value); otherwise clamp >= 0.
                  if (raw === "") {
                    handleWeightChange(key, "");
                  } else {
                    handleWeightChange(key, String(Math.max(0, Number(raw))));
                  }
                }}
              />
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
        <Button
          variant="contained"
          onClick={rerunCurrent}
          disabled={loading || applying || !query.trim()}
          sx={{ textTransform: "none" }}
        >
          Run Search
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleApply}
          disabled={loading || applying || !buildConfigOverride(weightOverrides)}
          sx={{ textTransform: "none" }}
        >
          {applying ? "Applying…" : "Apply Settings"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={loading || applying}
          sx={{ textTransform: "none" }}
        >
          Reset
        </Button>
        {applying && <CircularProgress size={20} />}
      </Box>

      {error && (
        <Typography sx={{ mt: 1.5, fontSize: 13, color: "#c62828" }}>
          {error}
        </Typography>
      )}

      {!error && meta.mode && (
        <Typography sx={{ mt: 1.5, fontSize: 12, color: "#888" }}>
          {results.length} shown / {meta.total} matched · {meta.mode} · {meta.tookMs}ms
        </Typography>
      )}

      <Box sx={{ mt: 1 }}>
        {results.map((r) => (
          <Box
            key={`${r.docType}-${r.id}`}
            sx={{
              py: 1,
              px: 1.5,
              borderBottom: "1px solid #eee",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            {/* Column 1 — image */}
            <Box
              sx={{
                width: 66,
                height: 66,
                flexShrink: 0,
                borderRadius: 1,
                backgroundColor: "#f0f0f0",
              }}
            >
              {r.imageUrl && (
                <Box
                  component="img"
                  src={r.imageUrl}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: 66,
                    height: 66,
                    borderRadius: 1,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              )}
            </Box>

            {/* Column 2 — text / stats */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                  {r.title || "(untitled)"}
                </Typography>
                <Chip
                  label={r.docType}
                  size="small"
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Box>
              <Typography sx={{ fontSize: 15, color: "#1f1f1f" }}>
                {[
                  `Org: ${r.orgName || r.orgHandle || 'None'}`,
                ]
                  .filter(Boolean)
                  .join("  ·  ")}
              </Typography>
              <Typography sx={{ fontSize: 15, color: "#1f1f1f" }}>
                {[
                  r.narratorName && `Told By: ${r.narratorName}`,
                  r.createdByHandle && `Created by: @${r.createdByHandle}`
                ]
                  .filter(Boolean)
                  .join("  ·  ")}
              </Typography>
            </Box>

            {/* Column 3 — score + matched fields (right justified) */}
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 0.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  color: "#1f1f1f",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                score {typeof r.score === "number" ? r.score.toFixed(2) : "-"}
              </Typography>
              {Array.isArray(r.matchedFields) && r.matchedFields.length > 0 && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#888",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.matchedFields
                    .map((f) => FIELD_LABELS[f] ?? f)
                    .join(" · ")}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SearchLabPanel;
